import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle, XCircle, ImageOff, Loader2, AlertTriangle } from 'lucide-react';
import { invoiceIntakeService } from '@/lib/invoiceIntakeService';
import { useCompany } from '@/context/CompanyContext';
import { useAuth } from '@/features/auth';

const statusConfig = {
  pending_review: { label: 'ממתין לסקירה', bg: 'bg-amber-100 text-amber-800' },
  confirmed: { label: 'אושר', bg: 'bg-green-100 text-green-800' },
  rejected: { label: 'נדחה', bg: 'bg-red-100 text-red-800' },
} as const;

const matchConfig = {
  sku: { label: 'התאמה לפי מק"ט', cls: 'bg-green-100 text-green-700' },
  barcode: { label: 'התאמה לפי ברקוד', cls: 'bg-green-100 text-green-700' },
  name: { label: 'התאמה לפי שם', cls: 'bg-amber-100 text-amber-700' },
  none: { label: 'לא הותאם', cls: 'bg-red-100 text-red-700' },
} as const;

function MatchBadge({ type, confidence }: { type: string; confidence?: number }) {
  const cfg = matchConfig[type as keyof typeof matchConfig] || matchConfig.none;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
      {type === 'name' && confidence != null && ` (${Math.round(confidence * 100)}%)`}
    </span>
  );
}

export function InvoiceReviewPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeCompany } = useCompany();
  const { user } = useAuth();

  const [invoice, setInvoice] = useState<any>(null);
  const [lines, setLines] = useState<any[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedQuantities, setEditedQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!invoiceId) return;
    (async () => {
      try {
        const data = await invoiceIntakeService.getInvoiceWithLines(invoiceId);
        if (!data.invoice || !data.lines) { setInvoice(null); return; }
        setInvoice(data.invoice);
        setLines(data.lines);
        const initial: Record<string, number> = {};
        data.lines.forEach((l: any) => {
          initial[l.id] = l.quantity_received ?? l.quantity_invoiced;
        });
        setEditedQuantities(initial);
        if (data.invoice.image_storage_path) {
          const url = await invoiceIntakeService.getImageUrl(data.invoice.image_storage_path);
          setImageUrl(url);
        }
      } catch {
        setInvoice(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [invoiceId]);

  const isPending = invoice?.status === 'pending_review';

  const handleConfirm = async () => {
    if (!invoice || saving) return;
    setSaving(true);
    try {
      for (const line of lines) {
        const qty = editedQuantities[line.id] ?? line.quantity_invoiced;
        if (qty !== line.quantity_invoiced) {
          await invoiceIntakeService.createDiscrepancy({
            line_id: line.id,
            invoice_id: invoice.id,
            discrepancy_type: qty < line.quantity_invoiced ? 'shortage' : 'surplus',
            quantity_expected: line.quantity_invoiced,
            quantity_actual: qty,
            amount_difference: (qty - (line.quantity_invoiced ?? 0)) * (line.unit_price ?? 0),
            company_id: activeCompany!.id,
          });
        }
      }
      await invoiceIntakeService.confirmInvoice(invoice.id, user!.id);
      navigate('/app/invoice-intake/history');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!invoice || saving) return;
    setSaving(true);
    try {
      await invoiceIntakeService.rejectInvoice(invoice.id);
      navigate('/app/invoice-intake/history');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="h-10 w-48 bg-gray-200 rounded-lg animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-96 bg-gray-200 rounded-xl animate-pulse" />
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="max-w-6xl mx-auto text-center py-20">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-lg text-gray-600">{t('invoiceReview.error', 'שגיאה בטעינת החשבונית')}</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-teal-600 hover:underline">
          {t('common.back', 'חזור')}
        </button>
      </div>
    );
  }

  const status = statusConfig[invoice.status as keyof typeof statusConfig] || statusConfig.pending_review;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowRight className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('invoiceReview.title', 'סקירת חשבונית')}
          </h1>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.bg}`}>{status.label}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-center min-h-[24rem]">
          {imageUrl ? (
            <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="block">
              <img src={imageUrl} alt="invoice" className="max-h-[32rem] object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity" />
            </a>
          ) : (
            <div className="text-center text-gray-400">
              <ImageOff className="w-16 h-16 mx-auto mb-2" />
              <p>{t('invoiceReview.noImage', 'אין תמונה זמינה')}</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-800">{t('invoiceReview.details', 'פרטי חשבונית')}</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Detail label="ספק" value={invoice.supplier_name_raw} />
            <Detail label="מספר חשבונית" value={invoice.invoice_number} />
            <Detail label="תאריך" value={invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('he-IL') : '—'} />
            <Detail label="סה״כ" value={invoice.total_amount != null ? `${Number(invoice.total_amount).toLocaleString('he-IL')}` : '—'} />
            <Detail label="מע״מ" value={invoice.vat_amount != null ? `${Number(invoice.vat_amount).toLocaleString('he-IL')}` : '—'} />
            <Detail label="מטבע" value={invoice.currency || '₪'} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <h2 className="text-lg font-semibold text-gray-800 p-6 pb-4">{t('invoiceReview.lines', 'שורות חשבונית')}</h2>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-right font-medium">#</th>
                <th className="px-4 py-3 text-right font-medium">תיאור</th>
                <th className="px-4 py-3 text-right font-medium">מק&quot;ט</th>
                <th className="px-4 py-3 text-right font-medium">כמות בחשבונית</th>
                <th className="px-4 py-3 text-right font-medium">כמות שהתקבלה</th>
                <th className="px-4 py-3 text-right font-medium">מחיר יחידה</th>
                <th className="px-4 py-3 text-right font-medium">סה&quot;כ שורה</th>
                <th className="px-4 py-3 text-right font-medium">סטטוס התאמה</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((line, i) => {
                const qty = editedQuantities[line.id] ?? line.quantity_invoiced;
                const mismatch = qty !== line.quantity_invoiced;
                return (
                  <tr key={line.id} className={mismatch ? 'bg-amber-50' : ''}>
                    <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{line.description}</td>
                    <td className="px-4 py-3 text-gray-600">{line.sku || '—'}</td>
                    <td className="px-4 py-3">{line.quantity_invoiced}</td>
                    <td className="px-4 py-3">
                      {isPending ? (
                        <input
                          type="number"
                          min={0}
                          value={qty}
                          onChange={(e) => setEditedQuantities((prev) => ({ ...prev, [line.id]: Number(e.target.value) }))}
                          className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-center focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                      ) : (
                        qty
                      )}
                    </td>
                    <td className="px-4 py-3">{line.unit_price != null ? Number(line.unit_price).toLocaleString('he-IL') : '—'}</td>
                    <td className="px-4 py-3">{line.line_total != null ? Number(line.line_total).toLocaleString('he-IL') : '—'}</td>
                    <td className="px-4 py-3"><MatchBadge type={line.match_type} confidence={line.match_confidence} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-100 px-4 pb-4">
          {lines.map((line, i) => {
            const qty = editedQuantities[line.id] ?? line.quantity_invoiced;
            const mismatch = qty !== line.quantity_invoiced;
            return (
              <div key={line.id} className={`py-4 space-y-2 ${mismatch ? 'bg-amber-50 -mx-4 px-4 rounded-lg' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800">{i + 1}. {line.description}</span>
                  <MatchBadge type={line.match_type} confidence={line.match_confidence} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <span>מק&quot;ט: {line.sku || '—'}</span>
                  <span>מחיר: {line.unit_price != null ? Number(line.unit_price).toLocaleString('he-IL') : '—'}</span>
                  <span>כמות בחשבונית: {line.quantity_invoiced}</span>
                  <span>סה&quot;כ: {line.line_total != null ? Number(line.line_total).toLocaleString('he-IL') : '—'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">כמות שהתקבלה:</span>
                  {isPending ? (
                    <input
                      type="number"
                      min={0}
                      value={qty}
                      onChange={(e) => setEditedQuantities((prev) => ({ ...prev, [line.id]: Number(e.target.value) }))}
                      className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-center focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  ) : (
                    <span>{qty}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isPending && (
        <div className="flex gap-3 justify-end">
          <button
            onClick={handleReject}
            disabled={saving}
            className="px-6 py-2.5 border-2 border-red-300 text-red-700 rounded-xl font-medium hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            {t('invoiceReview.reject', 'דחה חשבונית')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="px-6 py-2.5 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {t('invoiceReview.confirm', 'אשר חשבונית')}
          </button>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-gray-500 text-xs mb-1">{label}</dt>
      <dd className="font-medium text-gray-800">{value || '—'}</dd>
    </div>
  );
}
